import {
  useCallback,
  useEffect,
  useState,
} from "react";


import {
  useNavigate,
  useParams,
} from "react-router-dom";


/*
|--------------------------------------------------------------------------
| OFFERING HOOK
|--------------------------------------------------------------------------
*/

import {
  useOfferings,
} from "../hooks/useOfferings";


/*
|--------------------------------------------------------------------------
| OFFERING COMPONENTS
|--------------------------------------------------------------------------
*/

import OfferingDetails
  from "../components/OfferingDetails";


import OfferingImages
  from "../components/OfferingImages";


import OfferingPricingPlans
  from "../components/OfferingPricingPlans";


/*
|--------------------------------------------------------------------------
| OFFERING API
|--------------------------------------------------------------------------
*/

import {
  getOfferingApi,
} from "../../../api/offering/offering.api";


/*
|--------------------------------------------------------------------------
| OFFERING IMAGES API
|--------------------------------------------------------------------------
*/

import {
  getOfferingImagesApi,
  createOfferingImageApi,
  updateOfferingImageApi,
  deleteOfferingImageApi,
} from "../../../api/offering-images/offering-images.api";


/*
|--------------------------------------------------------------------------
| PRICING PLAN API
|--------------------------------------------------------------------------
*/

import {
  getPricingPlansApi,
  createPricingPlanApi,
  updatePricingPlanApi,
  deletePricingPlanApi,
} from "../../../api/pricing-plans/pricing-plans.api";


/*
|--------------------------------------------------------------------------
| PAGE
|--------------------------------------------------------------------------
*/

const OfferingDetailsPage = () => {

  /*
  |--------------------------------------------------------------------------
  | ROUTER
  |--------------------------------------------------------------------------
  */

  const {
    id,
  } = useParams();


  const navigate =
    useNavigate();


  /*
  |--------------------------------------------------------------------------
  | OFFERING HOOK
  |--------------------------------------------------------------------------
  */

  const {
    offering: hookOffering,
    detailsLoading,
  } = useOfferings();


  /*
  |--------------------------------------------------------------------------
  | OFFERING
  |--------------------------------------------------------------------------
  */

  const [
    offering,
    setOffering
  ] = useState(
    hookOffering || null
  );


  /*
  |--------------------------------------------------------------------------
  | IMAGES
  |--------------------------------------------------------------------------
  */

  const [
    images,
    setImages
  ] = useState([]);


  /*
  |--------------------------------------------------------------------------
  | PRICING PLANS
  |--------------------------------------------------------------------------
  */

  const [
    pricingPlans,
    setPricingPlans
  ] = useState([]);


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  const [
    loadingRelated,
    setLoadingRelated
  ] = useState(true);


  const [
    pricingLoading,
    setPricingLoading
  ] = useState(false);


  const [
    pricingSubmitting,
    setPricingSubmitting
  ] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | IMAGE SUBMITTING
  |--------------------------------------------------------------------------
  */

  const [
    imageSubmitting,
    setImageSubmitting
  ] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  const [
    error,
    setError
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | UPDATE OFFERING FROM HOOK
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (hookOffering) {

      setOffering(
        hookOffering
      );

    }

  }, [
    hookOffering
  ]);


  /*
  |--------------------------------------------------------------------------
  | LOAD OFFERING
  |--------------------------------------------------------------------------
  */

  const loadOffering =
    useCallback(
      async () => {

        if (!id) {

          return null;

        }


        const response =
          await getOfferingApi(
            id
          );


        const data =
          response?.data?.data ??
          response?.data ??
          response ??
          null;


        setOffering(
          data
        );


        return data;

      },
      [id]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD IMAGES
  |--------------------------------------------------------------------------
  */

  const loadImages =
    useCallback(
      async () => {

        if (!id) {

          return [];

        }


        try {

          const response =
            await getOfferingImagesApi(
              id
            );


          const data =
            response?.data?.data ??
            response?.data ??
            response ??
            [];


          const normalizedData =
            Array.isArray(data)
              ? data
              : [];


          setImages(
            normalizedData
          );


          return normalizedData;

        } catch (err) {

          console.error(
            "Failed to fetch offering images:",
            err
          );


          setImages([]);


          throw err;

        }

      },
      [id]
    );


  /*
  |--------------------------------------------------------------------------
  | CREATE OFFERING IMAGE
  |--------------------------------------------------------------------------
  */

  const handleCreateOfferingImage =
    async (
      formData
    ) => {

      const offeringId =
        offering?.id || id;


      if (!offeringId) {

        throw new Error(
          "Offering ID is required."
        );

      }


      if (
        !(formData instanceof FormData)
      ) {

        throw new Error(
          "Image upload data is required."
        );

      }


      try {

        setImageSubmitting(
          true
        );


        const response =
          await createOfferingImageApi(
            offeringId,
            formData
          );


        await loadImages();


        return (
          response?.data?.data ??
          response?.data ??
          response
        );

      } finally {

        setImageSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | UPDATE OFFERING IMAGE
  |--------------------------------------------------------------------------
  */

  const handleUpdateOfferingImage =
    async (
      imageId,
      payload
    ) => {

      if (!imageId) {

        throw new Error(
          "Offering image ID is required."
        );

      }


      if (
        !payload ||
        typeof payload !== "object" ||
        Array.isArray(payload)
      ) {

        throw new Error(
          "Offering image data is required."
        );

      }


      try {

        setImageSubmitting(
          true
        );


        const response =
          await updateOfferingImageApi(
            imageId,
            payload
          );


        await loadImages();


        return (
          response?.data?.data ??
          response?.data ??
          response
        );

      } finally {

        setImageSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | DELETE OFFERING IMAGE
  |--------------------------------------------------------------------------
  */

  const handleDeleteOfferingImage =
    async (
      image
    ) => {

      if (!image?.id) {

        throw new Error(
          "Offering image ID is required."
        );

      }


      try {

        setImageSubmitting(
          true
        );


        await deleteOfferingImageApi(
          image.id
        );


        await loadImages();

      } finally {

        setImageSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | LOAD PRICING PLANS
  |--------------------------------------------------------------------------
  */

  const loadPricingPlans =
    useCallback(
      async () => {

        if (!id) {

          return [];

        }


        try {

          setPricingLoading(
            true
          );


          const response =
            await getPricingPlansApi(
              id,
              true
            );


          const data =
            response?.data?.data ??
            response?.data ??
            response ??
            [];


          const normalizedData =
            Array.isArray(data)
              ? data
              : [];


          setPricingPlans(
            normalizedData
          );


          return normalizedData;

        } catch (err) {

          console.error(
            "Failed to fetch pricing plans:",
            err
          );


          setPricingPlans([]);


          throw err;

        } finally {

          setPricingLoading(
            false
          );

        }

      },
      [id]
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!id) {

      return;

    }


    let mounted = true;


    const loadPage =
      async () => {

        try {

          setError(null);


          setLoadingRelated(
            true
          );


          await loadOffering();


          if (!mounted) {

            return;

          }


          await Promise.all([
            loadImages(),
            loadPricingPlans(),
          ]);

        } catch (err) {

          console.error(
            "Failed to load offering:",
            err
          );


          if (mounted) {

            setError(
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              "Failed to load offering"
            );

          }

        } finally {

          if (mounted) {

            setLoadingRelated(
              false
            );

          }

        }

      };


    loadPage();


    return () => {

      mounted = false;

    };

  }, [
    id,
    loadOffering,
    loadImages,
    loadPricingPlans,
  ]);


  /*
  |--------------------------------------------------------------------------
  | CREATE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const handleCreatePricingPlan =
    async (
      firstArgument,
      secondArgument
    ) => {

      let offeringId;
      let payload;


      if (
        firstArgument &&
        typeof firstArgument === "object" &&
        !Array.isArray(firstArgument)
      ) {

        offeringId =
          offering?.id || id;


        payload =
          firstArgument;

      } else {

        offeringId =
          firstArgument ||
          offering?.id ||
          id;


        payload =
          secondArgument;

      }


      if (!offeringId) {

        throw new Error(
          "Offering ID is required."
        );

      }


      if (
        !payload ||
        typeof payload !== "object" ||
        Array.isArray(payload)
      ) {

        throw new Error(
          "Pricing plan data is required."
        );

      }


      if (
        !payload.name ||
        !String(
          payload.name
        ).trim()
      ) {

        throw new Error(
          "Plan name is required."
        );

      }


      try {

        setPricingSubmitting(
          true
        );


        const response =
          await createPricingPlanApi(
            offeringId,
            payload
          );


        await loadPricingPlans();


        return (
          response?.data?.data ??
          response?.data ??
          response
        );

      } finally {

        setPricingSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | UPDATE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const handleUpdatePricingPlan =
    async (
      planId,
      payload
    ) => {

      const offeringId =
        offering?.id || id;


      if (!offeringId) {

        throw new Error(
          "Offering ID is required."
        );

      }


      if (!planId) {

        throw new Error(
          "Pricing plan ID is required."
        );

      }


      if (
        !payload ||
        typeof payload !== "object" ||
        Array.isArray(payload)
      ) {

        throw new Error(
          "Pricing plan data is required."
        );

      }


      try {

        setPricingSubmitting(
          true
        );


        const response =
          await updatePricingPlanApi(
            planId,
            payload
          );


        await loadPricingPlans();


        return (
          response?.data?.data ??
          response?.data ??
          response
        );

      } finally {

        setPricingSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | DELETE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const handleDeletePricingPlan =
    async (
      plan
    ) => {

      const offeringId =
        offering?.id || id;


      if (!offeringId) {

        throw new Error(
          "Offering ID is required."
        );

      }


      if (!plan?.id) {

        throw new Error(
          "Pricing plan ID is required."
        );

      }


      try {

        setPricingSubmitting(
          true
        );


        await deletePricingPlanApi(
          plan.id
        );


        await loadPricingPlans();

      } finally {

        setPricingSubmitting(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (
    detailsLoading ||
    loadingRelated
  ) {

    return (

      <div className="loading">

        Loading offering...

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error) {

    return (

      <div className="error-message">

        <p>
          {error}
        </p>


        <button
          type="button"
          onClick={() =>
            navigate(
              "/offerings"
            )
          }
        >

          Back to Offerings

        </button>

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | NOT FOUND
  |--------------------------------------------------------------------------
  */

  if (!offering) {

    return (

      <div className="empty-state">

        <p>
          Offering not found.
        </p>


        <button
          type="button"
          onClick={() =>
            navigate(
              "/offerings"
            )
          }
        >

          Back to Offerings

        </button>

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | PAGE
  |--------------------------------------------------------------------------
  */

  return (

    <div className="offering-details-page">


      {/* PAGE HEADER */}

      <div className="page-header">

        <div>

          <h1>
            {offering.name}
          </h1>

          <p>
            Offering details
          </p>

        </div>


        <div className="page-actions">

          <button
            type="button"
            onClick={() =>
              navigate(
                `/offerings/${offering.id}/edit`
              )
            }
          >

            Edit

          </button>


          <button
            type="button"
            onClick={() =>
              navigate(
                "/offerings"
              )
            }
          >

            Back

          </button>

        </div>

      </div>


      {/* OFFERING DETAILS */}

      <OfferingDetails
        offering={
          offering
        }
      />


      {/* OFFERING IMAGES */}

      <OfferingImages

        offeringId={
          offering.id
        }

        images={
          images
        }

        loading={
          loadingRelated
        }

        submitting={
          imageSubmitting
        }

        onCreate={
          handleCreateOfferingImage
        }

        onUpdate={
          handleUpdateOfferingImage
        }

        onDelete={
          handleDeleteOfferingImage
        }

        onRefresh={
          loadImages
        }

      />


      {/* PRICING PLANS */}

      <OfferingPricingPlans

        offeringId={
          offering.id
        }

        plans={
          pricingPlans
        }

        loading={
          pricingLoading
        }

        submitting={
          pricingSubmitting
        }

        onCreate={
          handleCreatePricingPlan
        }

        onUpdate={
          handleUpdatePricingPlan
        }

        onDelete={
          handleDeletePricingPlan
        }

        onRefresh={
          loadPricingPlans
        }

      />

    </div>

  );

};


export default OfferingDetailsPage;